export const DEFAULT_VIEW_ID = 'default'

export type DashboardCardPosition = {
  deviceId: string
  x: number
  y: number
}

export const DASHBOARD_CARD_WIDTH = 280
export const DASHBOARD_CARD_GAP = 12
export const DASHBOARD_CARD_ROW_HEIGHT = 300
export const DASHBOARD_CANVAS_MIN_HEIGHT = 320

export type DashboardView = {
  id: string
  name: string
  deviceIds: string[]
  positions: DashboardCardPosition[]
  updatedAt: string
}

export type DashboardViewsState = {
  views: DashboardView[]
  activeViewId: string
}

export type DashboardViewDraft = Pick<DashboardView, 'name' | 'deviceIds' | 'positions'>

export const nextViewName = (existingNames: string[], nameFor: (number: number) => string): string => {
  const taken = new Set(existingNames.map((name) => name.trim().toLowerCase()))
  let number = existingNames.length + 1
  while (taken.has(nameFor(number).trim().toLowerCase())) number += 1
  return nameFor(number)
}

export const automaticPositions = (deviceIds: string[], columns = 3): DashboardCardPosition[] =>
  deviceIds.map((deviceId, index) => ({
    deviceId,
    x: (index % Math.max(1, columns)) * (DASHBOARD_CARD_WIDTH + DASHBOARD_CARD_GAP),
    y: Math.floor(index / Math.max(1, columns)) * (DASHBOARD_CARD_ROW_HEIGHT + DASHBOARD_CARD_GAP),
  }))

export const normalizePositions = (
  deviceIds: string[],
  positions: DashboardCardPosition[],
): DashboardCardPosition[] => {
  const allowed = new Set(deviceIds)
  const ordered = positions
    .filter(
      (position, index, list) =>
        allowed.has(position.deviceId) &&
        Number.isFinite(position.x) &&
        Number.isFinite(position.y) &&
        list.findIndex((item) => item.deviceId === position.deviceId) === index,
    )
    .map(({ deviceId }) => deviceId)

  deviceIds.forEach((id) => {
    if (!ordered.includes(id)) ordered.push(id)
  })

  const existing = new Map(positions.map((position) => [position.deviceId, position]))
  const defaults = automaticPositions(ordered)
  return defaults.map((fallback) => {
    const position = existing.get(fallback.deviceId)
    return position && Number.isFinite(position.x) && Number.isFinite(position.y)
      ? { deviceId: fallback.deviceId, x: Math.max(0, position.x), y: Math.max(0, position.y) }
      : fallback
  })
}

export const orderedDeviceIds = (view: Pick<DashboardView, 'deviceIds' | 'positions'>): string[] =>
  normalizePositions(view.deviceIds, view.positions).map(({ deviceId }) => deviceId)

export const createDraft = (view?: DashboardView): DashboardViewDraft => {
  const deviceIds = view?.deviceIds ?? []
  return {
    name: view?.name ?? '',
    deviceIds: [...deviceIds],
    positions: normalizePositions(deviceIds, view?.positions ?? []),
  }
}

export const draftsEqual = (left: DashboardViewDraft, right: DashboardViewDraft): boolean =>
  left.name.trim() === right.name.trim() &&
  JSON.stringify(left.deviceIds) === JSON.stringify(right.deviceIds) &&
  JSON.stringify(normalizePositions(left.deviceIds, left.positions)) ===
    JSON.stringify(normalizePositions(right.deviceIds, right.positions))
